import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  SW_NAVIGATE_EVENT,
  consumeStashedNavigateUrl,
  consumeStashedPushRedirect,
  resolveSwNavigationPath,
} from '../utils/swNavigationBridge';
import type { AppSurface } from '../appSurface';

const PUSH_REDIRECT_PARAM = 'pushRedirect';

/** P3-A12/PP12's receiving side, Router half — mount once inside
 * `<BrowserRouter>` (it needs `useNavigate`/`useLocation`). Handles both
 * hand-off paths `src/sw.ts`'s `notificationclick` can take:
 *
 *  1. A focused/open tab gets a `postMessage`, which `main.tsx` re-dispatches
 *     as a `valentine:sw-navigate` window CustomEvent (see
 *     `swNavigationBridge.ts`) — this hook subscribes to that event and
 *     routes client-side via react-router, never a full page navigation.
 *  2. No tab was open, so the SW opened a fresh window at
 *     `/?pushRedirect=<encoded path>` — this hook reads that param ONCE on
 *     mount (via `useLocation`, not `window.location`, so it works the same
 *     whether the app is running under a real `BrowserRouter` or a test
 *     `MemoryRouter`), navigates to the decoded target, and REPLACES history
 *     so the `?pushRedirect=` URL never becomes a back-button stop.
 */
export function useSwNavigationBridge(surface: AppSurface = 'valentine'): void {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Fix-batch item 2: a NAVIGATE message that arrived before this hook
    // mounted was stashed at the module level — consume it first, then
    // start listening live for anything that arrives after.
    const stashedNavigate = consumeStashedNavigateUrl(surface);
    if (stashedNavigate) {
      try {
        navigate(stashedNavigate);
      } catch {
        // defensive — see isSafeInternalPath's doc comment (fix-batch item 1)
      }
    }

    const onNavigate = (event: Event) => {
      const url = (event as CustomEvent<string>).detail;
      const target = resolveSwNavigationPath(url, surface);
      if (target) {
        try {
          navigate(target);
        } catch {
          // defensive — a malformed-but-passing-validation target should
          // never blank-screen the app
        }
      }
    };
    window.addEventListener(SW_NAVIGATE_EVENT, onNavigate as EventListener);
    return () => window.removeEventListener(SW_NAVIGATE_EVENT, onNavigate as EventListener);
  }, [navigate, surface]);

  useEffect(() => {
    // Fix-batch item 5: a stash written before an auth redirect
    // (AuthContext.login(), right before msalInstance.loginRedirect()) takes
    // priority — it survives the same-tab round trip that drops the URL's
    // own query string. Falls back to reading `?pushRedirect=` directly when
    // nothing was stashed (native login, or an SW-opened window that never
    // needed a redirect).
    const stashed = consumeStashedPushRedirect(surface);
    const redirect = stashed ?? new URLSearchParams(location.search).get(PUSH_REDIRECT_PARAM);
    if (!redirect) return;

    const target = resolveSwNavigationPath(redirect, surface);
    if (target) {
      try {
        navigate(target, { replace: true });
      } catch {
        // defensive — a malformed-but-passing-validation target should
        // never blank-screen the app
      }
      return;
    }

    // Fix-batch item 1: invalid target (open-redirect attempt / crafted
    // link) — never call navigate() with it. Strip the param from the
    // address bar instead so a bad link doesn't linger / get bookmarked
    // with the payload. (Nothing to strip when the value came from the
    // sessionStorage stash — that's already been cleared above.)
    if (!stashed) {
      const params = new URLSearchParams(location.search);
      params.delete(PUSH_REDIRECT_PARAM);
      const strippedSearch = params.toString();
      window.history.replaceState(
        null,
        '',
        location.pathname + (strippedSearch ? `?${strippedSearch}` : '') + location.hash,
      );
    }
    // Mount-only (P3-A12/PP12): the SW only ever sets this param on the
    // fresh window it itself opens — it must be consumed exactly once on
    // boot, not re-derived on every subsequent navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
